import { Action, Dispatch } from 'redux';
import { actionTypes } from 'redux-form';
import { ThunkAction } from 'redux-thunk';

import {
  LOAD_SCHEMA_START,
  LOAD_SCHEMA_ERROR,
  LOAD_SCHEMA_SUCCESS,
  SUBMIT_APPLICATION_START,
  SUBMIT_APPLICATION_ERROR,
  SUBMIT_APPLICATION_SUCCESS,
  CONTINUE_TO_FORM,
  VALIDATE_FORM_GROUP_SUCCESS,
  LoadSchemaStart,
  LoadSchemaError,
  LoadSchemaSuccess,
  SubmitApplicationStart,
  SubmitApplicationError,
  SubmitApplicationSucccess,
  ContinueToForm,
  ValidateFormGroupSuccess,
  StartAsyncValidation
} from './types';

import { ApplicationState } from '../types';
import { getFieldsIdsByPage, getAsyncGroups } from './wizardUtils';
import { createPageProgressMap } from '../application/actions';
import { parseURLParams } from '../../utils/urlReader';
import { FORM_SCHEMA_PARAM, FORM_CONTAINER_NAME } from '../../constants';
import {
  getSchema,
  submitForm,
  getAsyncFieldValidation
} from '../../utils/FormsClient';
import { stopAsyncValidation } from 'redux-form';

const BASIC_ERROR_MESSAGE =
  'Please refresh the page and try again, or contact your recruiter anytime.';
const NO_SCHEMA_PARAM_ERROR =
  'We cannot find the form you are looking for. Please verify you have the right address for the page';

export function continueToForm(): ContinueToForm {
  window.scroll(0, 0);
  return {
    type: CONTINUE_TO_FORM
  };
}

export function getSchemaStart(): LoadSchemaStart {
  return {
    type: LOAD_SCHEMA_START
  };
}

export function getSchemaFail(errorMsg: string): LoadSchemaError {
  return {
    type: LOAD_SCHEMA_ERROR,
    errorMsg
  };
}

export function getSchemaSuccess(
  schema: any,
  formIds: FormIds,
  asyncGroups: AsyncGroup[]
): LoadSchemaSuccess {
  return {
    type: LOAD_SCHEMA_SUCCESS,
    schema,
    asyncGroups,
    formIds
  };
}

export function submitApplicationStart(): SubmitApplicationStart {
  return {
    type: SUBMIT_APPLICATION_START
  };
}

export function submitApplicationSuccess(
  insertedId: number
): SubmitApplicationSucccess {
  return {
    type: SUBMIT_APPLICATION_SUCCESS,
    confirmationNumber: insertedId
  };
}

export function submitApplicationError(error: string): SubmitApplicationError {
  return {
    type: SUBMIT_APPLICATION_ERROR,
    error
  };
}

export function validateFormGroupStart(field: string): StartAsyncValidation {
  return {
    type: actionTypes.START_ASYNC_VALIDATION,
    meta: {
      field,
      form: FORM_CONTAINER_NAME
    }
  };
}

export function validateFormGroupSuccess(
  fields: string[]
): ValidateFormGroupSuccess {
  return {
    type: VALIDATE_FORM_GROUP_SUCCESS,
    fields
  };
}

export const validateFormGroup = async (
  groupToValidate: AsyncGroup,
  params: ObjectKeyStringKeyValues,
  dispatch: Dispatch
) => {
  const fieldIds = Object.keys(params);
  const finalFieldId: string =
    fieldIds.length > 0 ? fieldIds[fieldIds.length - 1] : '';
  dispatch(validateFormGroupStart(finalFieldId));
  try {
    const res = await getAsyncFieldValidation(groupToValidate.action, params);
    if (res && res.status === 200) {
      dispatch(stopAsyncValidation(FORM_CONTAINER_NAME));
    } else {
      groupToValidate.errorMessage =
        res.status > 404 ? 'An Error Occured' : groupToValidate.errorMessage;
      throw groupToValidate.errorMessage;
    }
  } catch (e) {
    const errorObj: ObjectKeyStringKeyValues = fieldIds.reduce(
      (acc: ObjectKeyStringKeyValues, id: string) => {
        acc[id] = ' ';
        return acc;
      },
      {}
    );
    errorObj[finalFieldId] = groupToValidate.errorMessage;
    dispatch(stopAsyncValidation(FORM_CONTAINER_NAME, errorObj));
  }
};

export const getFormSchema = (): ThunkAction<
  void,
  ApplicationState,
  null,
  Action<string>
> => async dispatch => {
  dispatch(getSchemaStart());
  try {
    const urlSearchParams = parseURLParams(window.location.href);

    if (!urlSearchParams[FORM_SCHEMA_PARAM]) {
      dispatch(getSchemaFail(NO_SCHEMA_PARAM_ERROR));
      return;
    }

    const schema = await getSchema(urlSearchParams);

    const formIdsByPage = getFieldsIdsByPage(schema);

    const asyncGroups = getAsyncGroups(schema);

    const formIds = formIdsByPage.reduce((acc: string[], fields: string[]) => {
      acc.push(...fields);
      return acc;
    }, []);

    if (formIds.length === 0) {
      dispatch(getSchemaFail(BASIC_ERROR_MESSAGE));

      return;
    }
    dispatch(getSchemaSuccess(schema, formIds, asyncGroups));
    dispatch(createPageProgressMap(false, formIdsByPage[0]));
  } catch (error) {
    dispatch(getSchemaFail(BASIC_ERROR_MESSAGE));
  }
};

export const submitApplication = (
  values: ObjectKeyStringKeyValues
): ThunkAction<void, ApplicationState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  dispatch(submitApplicationStart());
  try {
    const state = getState();

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const submitEndpoint = state.wizard.schema!.formAction || '';

    const response = await submitForm(submitEndpoint, values);

    dispatch(submitApplicationSuccess(response.insertedId));
  } catch (error) {
    dispatch(submitApplicationError(BASIC_ERROR_MESSAGE));
  }
};
